<template>
  <div 
    v-if="message"
    class="message-box-overlay"
    :class="{ 'overlay-dismissible': message.dismissible !== false }"
    @click="handleOverlayClick"
  >
    <div 
      class="message-box"
      :class="[
        `message-${message.type || 'normal'}`,
        { 'message-shake': shouldShake }
      ]"
      :style="messageBoxStyles"
      @click.stop
      @mousedown="pauseTimer"
      @mouseup="resumeTimer"
      @mouseleave="resumeTimer"
    >
      <!-- Close button -->
      <button 
        v-if="message.dismissible !== false"
        class="close-button"
        @click="handleClose"
        aria-label="Close message"
      >
        ×
      </button>

      <!-- Message content -->
      <div class="message-content">
        <!-- Icon based on message type -->
        <div class="message-icon" v-if="messageIcon">
          {{ messageIcon }}
        </div>

        <!-- Text content -->
        <div class="message-text" v-html="formattedText"></div>

        <!-- Media content -->
        <div v-if="message.media" class="message-media">
          <img 
            v-if="message.media.type === 'image'" 
            :src="message.media.src" 
            :alt="message.media.caption || 'Tutorial image'"
            class="media-image"
          />
          <div v-else-if="isYoutubeVideo" class="video-embed-container">
            <iframe
              :src="message.media.src"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
              class="media-video-embed"
            ></iframe>
          </div>
          <video 
            v-else-if="message.media.type === 'video'"
            :src="message.media.src"
            :controls="message.media.controls !== false"
            :autoplay="message.media.autoplay || false"
            class="media-video"
          ></video>
        </div>

        <!-- Question input -->
        <div v-if="message.isQuestion" class="question-input">
          <textarea
            v-if="message.multiline"
            v-model="userResponse"
            :placeholder="message.placeholder || 'Type your answer...'"
            class="input-field input-textarea"
            rows="3"
            @keydown.ctrl.enter="submitResponse"
          ></textarea>
          <input
            v-else
            v-model="userResponse"
            type="text"
            :placeholder="message.placeholder || 'Type your answer...'"
            class="input-field input-text"
            @keydown.enter="submitResponse"
          />
          
          <div class="question-buttons">
            <button 
              class="btn btn-primary"
              @click="submitResponse"
              :disabled="!userResponse.trim() && message.required !== false"
            >
              Submit
            </button>
            <button 
              v-if="!message.required"
              class="btn btn-secondary"
              @click="skipQuestion"
            >
              Skip
            </button>
          </div>
        </div>

        <!-- Action buttons -->
        <div v-if="message.actions" class="message-actions">
          <button
            v-for="action in message.actions"
            :key="action.id"
            class="btn"
            :class="`btn-${action.type || 'primary'}`"
            @click="handleAction(action)"
          >
            {{ action.label }}
          </button>
        </div>
      </div>

      <!-- Progress bar for timed messages -->
      <div 
        v-if="message.duration && message.type !== 'question'"
        class="progress-bar"
        :class="{ 'paused': isPaused }"
      >
        <div 
          class="progress-fill"
          :style="{ animationDuration: `${message.duration}ms` }"
        ></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import type { PecTutorConfig } from '../types';

// Props
interface Props {
  message: {
    text?: string;
    type?: string;
    duration?: number;
    position?: string;
    dismissible?: boolean;
    media?: {
      type: string;
      src: string;
      caption?: string;
      controls?: boolean;
      autoplay?: boolean;
    };
    isQuestion?: boolean;
    placeholder?: string;
    required?: boolean;
    multiline?: boolean;
    validationFn?: (input: string) => boolean | string;
    actions?: Array<{
      id: string;
      label: string;
      type?: string;
    }>;
  };
  config: PecTutorConfig;
}

const props = defineProps<Props>();

// Emits
const emit = defineEmits<{
  close: [];
  response: [value: string];
  action: [action: { id: string; label: string; type?: string }];
}>();

// Reactive state
const userResponse = ref('');
const shouldShake = ref(false);
const timeoutId = ref<number | null>(null);
const isPaused = ref(false);
const startTime = ref(0);
const remainingTime = ref(0);

// Computed properties
const messageIcon = computed(() => {
  const icons: Record<string, string> = {
    normal: '',
    attention: '⚠️',
    warning: '⚠️',
    tip: '💡',
    success: '✅',
    error: '❌',
    question: '❓'
  };
  return icons[props.message?.type || 'normal'] || '';
});

const isYoutubeVideo = computed(() => {
  return props.message.media?.type === 'video' && props.message.media.src.includes('youtube.com/embed');
});

const formattedText = computed(() => {
  if (!props.message?.text) return '';
  
  // Simple markdown-like formatting
  return props.message.text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`(.*?)`/g, '<code>$1</code>')
    .replace(/\n/g, '<br>');
});

  const messageBoxStyles = computed(() => {
    const position = props.message?.position || 'center';
    
    const positions: Record<string, Record<string, string>> = {
    top: { top: '20px', left: '50%', transform: 'translateX(-50%)' },
    center: { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
    bottom: { bottom: '20px', left: '50%', transform: 'translateX(-50%)' },
    'follow-mouse': { /* Will be handled by mouse position */ }
  };

  return {
    ...positions[position],
    maxWidth: '90vw',
    width: 'auto',
  };
});

// Methods
const handleClose = () => {
  clearAutoClose();
  emit('close');
};

const handleOverlayClick = () => {
  if (props.message?.dismissible !== false) {
    handleClose();
  }
};

const submitResponse = () => {
  if (props.message?.required !== false && !userResponse.value.trim()) {
    shake();
    return;
  }

  // Validate if validation function exists
  if (props.message?.validationFn) {
    const validation = props.message.validationFn(userResponse.value);
    if (validation !== true) {
      shake();
      // Show validation error if it's a string
      if (typeof validation === 'string') {
        console.warn('Validation error:', validation);
      }
      return;
    }
  }

  emit('response', userResponse.value);
  userResponse.value = '';
};

const skipQuestion = () => {
  emit('response', '');
  userResponse.value = '';
};

const handleAction = (action: { id: string; label: string; type?: string }) => {
  emit('action', action);
};

const shake = () => {
  shouldShake.value = true;
  setTimeout(() => {
    shouldShake.value = false;
  }, props.config.timeouts.attentionShake);
};

const setupAutoClose = () => {
  if (props.message?.duration && props.message?.type !== 'question') {
    startTimer(props.message.duration);
  }
};

const startTimer = (duration: number) => {
  clearAutoClose();
  startTime.value = Date.now();
  remainingTime.value = duration;
  timeoutId.value = window.setTimeout(() => {
    handleClose();
  }, duration);
};

const clearAutoClose = () => {
  if (timeoutId.value) {
    clearTimeout(timeoutId.value);
    timeoutId.value = null;
  }
};

const pauseTimer = () => {
  if (isPaused.value || !timeoutId.value) return;
  isPaused.value = true;
  clearAutoClose();
  const elapsedTime = Date.now() - startTime.value;
  remainingTime.value -= elapsedTime;
};

const resumeTimer = () => {
  if (!isPaused.value) return;
  isPaused.value = false;
  if (remainingTime.value > 0) {
    startTimer(remainingTime.value);
  }
};

// Watch for message changes
watch(() => props.message, (newMessage) => {
  clearAutoClose();
  userResponse.value = '';
  isPaused.value = false;
  
  if (newMessage) {
    setupAutoClose();
    
    // Handle attention type
    if (newMessage.type === 'attention') {
      shake();
    }
  }
}, { immediate: true });

// Lifecycle
onMounted(() => {
  // setupAutoClose is now called from the watcher
});

onUnmounted(() => {
  clearAutoClose();
});
</script>

<style scoped>
.message-box-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(2px);
  z-index: 10001;
  pointer-events: auto;
  animation: overlayAppear 0.2s ease;
}

.overlay-dismissible {
  cursor: pointer;
}

.message-box {
  position: absolute;
  background: var(--pec-tutor-background, white);
  border-radius: var(--pec-tutor-border-radius, 8px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  max-width: 500px;
  min-width: 300px;
  overflow: hidden;
  animation: messageAppear 0.3s ease;
  cursor: default;
}

/* Message types */
.message-normal {
  border-left: 4px solid var(--pec-tutor-primary, #007bff);
}

.message-attention {
  border-left: 4px solid #ff6b35;
  background: linear-gradient(135deg, #fff8f5 0%, white 100%);
}

.message-warning {
  border-left: 4px solid #ffa726;
  background: linear-gradient(135deg, #fffbf5 0%, white 100%);
}

.message-tip {
  border-left: 4px solid #66bb6a;
  background: linear-gradient(135deg, #f8fff8 0%, white 100%);
}

.message-success {
  border-left: 4px solid #4caf50;
  background: linear-gradient(135deg, #f8fff8 0%, white 100%);
}

.message-error {
  border-left: 4px solid #f44336;
  background: linear-gradient(135deg, #fff8f8 0%, white 100%);
}

.message-question {
  border-left: 4px solid #2196f3;
  background: linear-gradient(135deg, #f5f9ff 0%, white 100%);
}

.message-shake {
  animation: shake 0.5s ease-in-out;
}

/* Close button */
.close-button {
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  font-size: 24px;
  line-height: 1;
  color: #999;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.close-button:hover {
  background: #f5f5f5;
  color: #333;
}

/* Content */
.message-content {
  padding: 20px;
  padding-right: 50px;
}

.message-icon {
  font-size: 24px;
  margin-bottom: 10px;
}

.message-text {
  color: var(--pec-tutor-text, #333);
  line-height: 1.5;
  margin-bottom: 15px;
}

.message-text:last-child {
  margin-bottom: 0;
}

/* Media */
.message-media {
  margin: 15px 0;
}

.media-image {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
}

.media-video {
  width: 100%;
  max-height: 300px;
  border-radius: 4px;
}

.video-embed-container {
  position: relative;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  height: 0;
  overflow: hidden;
  max-width: 100%;
  background: #000;
  border-radius: 4px;
}

.media-video-embed {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* Question input */
.question-input {
  margin-top: 15px;
}

.input-field {
  width: 100%;
  padding: 12px;
  border: 2px solid #e1e5e9;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  margin-bottom: 10px;
}

.input-field:focus {
  outline: none;
  border-color: var(--pec-tutor-primary, #007bff);
}

.input-textarea {
  resize: vertical;
  min-height: 80px;
}

.question-buttons {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

/* Buttons */
.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--pec-tutor-primary, #007bff);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

.btn-secondary {
  background: var(--pec-tutor-secondary, #6c757d);
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #545b62;
}

/* Actions */
.message-actions {
  margin-top: 15px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

/* Progress bar */
.progress-bar {
  height: 3px;
  background: #e1e5e9;
  overflow: hidden;
}

.progress-bar.paused .progress-fill {
  animation-play-state: paused;
}

.progress-fill {
  height: 100%;
  background: var(--pec-tutor-primary, #007bff);
  animation: progressFill linear forwards;
  transform-origin: left;
}

/* Animations */
@keyframes overlayAppear {
  0% { opacity: 0; }
  100% { opacity: 1; }
}

@keyframes messageAppear {
  0% { 
    opacity: 0; 
    transform: translate(-50%, -50%) scale(0.9);
  }
  100% { 
    opacity: 1; 
    transform: translate(-50%, -50%) scale(1);
  }
}

@keyframes shake {
  0%, 100% { transform: translate(-50%, -50%) translateX(0); }
  25% { transform: translate(-50%, -50%) translateX(-5px); }
  75% { transform: translate(-50%, -50%) translateX(5px); }
}

@keyframes progressFill {
  0% { transform: scaleX(1); }
  100% { transform: scaleX(0); }
}

/* Responsive */
@media (max-width: 768px) {
  .message-box {
    max-width: 95vw;
    min-width: 280px;
  }
  
  .message-content {
    padding: 15px;
    padding-right: 40px;
  }
}
</style>
